/**
 * Theme Context
 * Manages theme state and provides theme switching functionality
 * Syncs with user preferences when authenticated
 */

import { DEFAULT_THEME_ID } from '@parlay-picker/shared/themes';
import {
  createContext,
  type ReactElement,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { getTheme, type Theme, themes } from '../config/themes';

/** Map removed theme IDs to their replacement */
const THEME_MIGRATIONS: Record<string, string> = {
  rebrand: 'arctic-cyan',
  'lakers-gold': 'royal-flush',
  'celtics-green': 'lucky-streak',
  'warriors-blue': 'golden-gate',
  'heat-red': 'crimson-edge',
  inferno: 'crimson-edge',
  'emerald-forest': 'emerald-prestige',
};

interface ThemeContextType {
  theme: Theme;
  themeId: string;
  setTheme: (themeId: string) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_STORAGE_KEY = 'parlay-picker-theme';

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps): ReactElement {
  const [themeId, setThemeId] = useState<string>(() => {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (!stored) return DEFAULT_THEME_ID;
    // Migrate removed theme IDs to their replacements
    if (THEME_MIGRATIONS[stored]) return THEME_MIGRATIONS[stored];
    // Validate theme exists, fall back to default if not
    return themes[stored] ? stored : DEFAULT_THEME_ID;
  });
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const theme = getTheme(themeId);

  // Listen for chat-initiated theme updates
  // biome-ignore lint/correctness/useExhaustiveDependencies: setTheme is stable (only calls setThemeId)
  useEffect(() => {
    const handler = (e: Event) => {
      const themeId = (e as CustomEvent<string>).detail;
      if (typeof themeId === 'string') {
        setTheme(themeId);
      }
    };
    window.addEventListener('chat-update-theme', handler);
    return () => window.removeEventListener('chat-update-theme', handler);
  }, []);

  // Apply theme CSS variables to document root
  useEffect(() => {
    const root = document.documentElement;

    // Disable transitions on initial load to prevent flash
    if (isInitialLoad) {
      root.classList.add('theme-loading');
    }

    // Set all theme color variables
    Object.entries(theme.colors).forEach(([key, value]) => {
      const cssVarName = `--color-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
      root.style.setProperty(cssVarName, value);
    });

    // Set font variables (custom fonts for themes that define them, system fonts otherwise)
    if (theme.fonts) {
      root.style.setProperty('--font-heading', theme.fonts.heading);
      root.style.setProperty('--font-body', theme.fonts.body);
      root.style.setProperty('--font-mono', theme.fonts.mono);
    } else {
      root.style.setProperty('--font-heading', 'system-ui, -apple-system, sans-serif');
      root.style.setProperty('--font-body', 'system-ui, -apple-system, sans-serif');
      root.style.setProperty('--font-mono', 'ui-monospace, monospace');
    }

    // Store theme preference in localStorage as fallback
    localStorage.setItem(THEME_STORAGE_KEY, themeId);

    // Set data attribute for CSS selector usage
    root.setAttribute('data-theme', themeId);

    // Re-enable transitions after initial load
    if (isInitialLoad) {
      // Small delay to ensure CSS variables are applied before enabling transitions
      requestAnimationFrame(() => {
        root.classList.remove('theme-loading');
        setIsInitialLoad(false);
      });
    }
  }, [themeId, theme, isInitialLoad]);

  const setTheme = (newThemeId: string): void => {
    // Migrate removed theme IDs, validate existence
    const migrated = THEME_MIGRATIONS[newThemeId] ?? newThemeId;
    setThemeId(themes[migrated] ? migrated : DEFAULT_THEME_ID);
  };

  const toggleTheme = (): void => {
    // Cycle through themes
    const themeIds = [
      'dark-electric',
      'midnight-blue',
      'sunset-amber',
      'violet-dream',
      'royal-flush',
      'lucky-streak',
      'golden-gate',
      'arctic-cyan',
      'crimson-edge',
      'emerald-prestige',
      'high-contrast',
      'light-court',
    ];
    const currentIndex = themeIds.indexOf(themeId);
    const nextIndex = (currentIndex + 1) % themeIds.length;
    setThemeId(themeIds[nextIndex]);
  };

  return (
    <ThemeContext.Provider value={{ theme, themeId, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
